import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Stack,
  IconButton
} from '@mui/material';
import { Label, Category, Close } from '@mui/icons-material';

function ProductEditModal({ open, onClose, product, onSave, onDelete, isSaving, onManageAttributes, onManageCategories }) {
  const [editedProduct, setEditedProduct] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isNewProduct = !product?.id;

  useEffect(() => {
    if (product) {
      setEditedProduct({
        ...product,
        nome: product.nome || product.name || '',
        descrizione_breve: product.descrizione_breve || product.description || '',
        descrizione_lunga: product.descrizione_lunga || '',
        prezzo: product.prezzo || product.price || '',
        imageUrl: product.imageUrl || '',
        sku: product.sku || '',
        slug: product.slug || '',
        visibile: product.visibile == 1 || String(product.visibile).toLowerCase() === 'true',
        in_evidenza: product.in_evidenza == 1 || String(product.in_evidenza).toLowerCase() === 'true',
      });
    } else {
      setEditedProduct(null);
    }
    setIsDirty(false);
    setIsDeleting(false);
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setEditedProduct(prev => ({ ...prev, [name]: finalValue }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(editedProduct);
  };

  const handleDelete = async () => {
    if (window.confirm(`Sei sicuro di voler eliminare definitivamente il prodotto "${editedProduct.nome}"?`)) {
      setIsDeleting(true);
      try {
        await onDelete(editedProduct.id);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleClose = () => {
    if (!isSaving && !isDeleting) {
      onClose();
    }
  };

  if (!editedProduct) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close" sx={{ mr: 2 }}>
            <Close />
          </IconButton>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {isNewProduct ? 'Crea Nuovo Prodotto' : `Modifica Prodotto: ${editedProduct.nome} (ID: ${editedProduct.id})`}
          </Typography>
        </Box>
        {!isNewProduct && (
          <Stack direction="row" spacing={1}>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<Label />} 
              onClick={() => onManageAttributes(product)}
            >
              Attributi
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<Category />} 
              onClick={() => onManageCategories(product)}
            >
              Categorie
            </Button>
          </Stack>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {!isNewProduct && (
                <TextField
                  fullWidth
                  disabled
                  label="ID Prodotto"
                  value={editedProduct.id}
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="filled"
                  helperText="L'ID non è modificabile"
                />
              )}
              <TextField
                fullWidth
                label="Nome Prodotto"
                name="nome"
                value={editedProduct.nome}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Prezzo"
                name="prezzo"
                value={editedProduct.prezzo}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={editedProduct.sku}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug"
                name="slug"
                value={editedProduct.slug}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="URL Immagine"
                name="imageUrl"
                value={editedProduct.imageUrl}
                onChange={handleChange}
                margin="normal"
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControlLabel
                  control={<Checkbox name="visibile" checked={editedProduct.visibile} onChange={handleChange} color="primary" />}
                  label="Visibile"
                />
                <FormControlLabel
                  control={<Checkbox name="in_evidenza" checked={editedProduct.in_evidenza} onChange={handleChange} color="primary" />}
                  label="In Evidenza"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione Breve"
                name="descrizione_breve"
                value={editedProduct.descrizione_breve}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Descrizione Lunga"
                name="descrizione_lunga"
                value={editedProduct.descrizione_lunga}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Box>
          {!isNewProduct && (
            <Button 
              color="error" 
              onClick={handleDelete} 
              disabled={isSaving || isDeleting}
            >
              {isDeleting ? 'Eliminazione...' : 'Elimina Prodotto'}
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={handleClose} disabled={isSaving || isDeleting}>Annulla</Button>
          <Button onClick={handleSave} variant="contained" disabled={!isDirty || isSaving || isDeleting}>
            {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ProductEditModal;
